<template>
  <div>
    <div class="page-hero">
      <div class="container">
        <span class="section__label">Документация</span>
        <h1 class="page-hero__title">Руководство пользователя</h1>
        <p class="page-hero__text">Всё необходимое для быстрого старта и работы со CandB.pro</p>
      </div>
    </div>

    <section class="section">
      <div class="container docs-layout">
        <aside class="docs-nav-wrap">
          <details class="docs-nav-mobile" :open="!isMobile">
            <summary class="docs-nav-mobile__summary">
              <span>{{ current ? current.article.title : 'Содержание' }}</span>
              <span class="docs-nav-mobile__chevron">▾</span>
            </summary>
            <nav class="docs-nav">
              <div v-for="section in sections" :key="section.slug">
                <div class="docs-nav__group">{{ section.title }}</div>
                <router-link
                  v-for="article in section.articles"
                  :key="article.slug"
                  :to="`/docs/${section.slug}/${article.slug}`"
                  class="docs-nav__link"
                  active-class="active"
                >{{ article.title }}</router-link>
              </div>
            </nav>
          </details>
        </aside>

        <div class="docs-content">
          <div v-if="current" class="docs-content__header">
            <div class="docs-content__breadcrumb">{{ current.section.title }}</div>
            <h2>{{ current.article.title }}</h2>
          </div>
          <div v-if="loading" class="docs-content__body docs-content__body--state">
            <p>Загружаем статью…</p>
          </div>
          <div v-else-if="error" class="docs-content__body docs-content__body--state">
            <p>Не удалось загрузить статью. Попробуйте обновить страницу или выберите другой раздел.</p>
            <p>Если ошибка повторяется — напишите на <a href="mailto:info@candb.pro">info@candb.pro</a>.</p>
          </div>
          <div
            v-else
            class="docs-content__body markdown-body"
            v-html="html"
            @click="onContentClick"
          />

          <div v-if="current && neighbors" class="docs-nav-bottom">
            <router-link
              v-if="neighbors.prev"
              :to="`/docs/${neighbors.prev.section.slug}/${neighbors.prev.article.slug}`"
              class="docs-nav-bottom__link"
            >
              <span class="docs-nav-bottom__label">← Предыдущая</span>
              <span class="docs-nav-bottom__title">{{ neighbors.prev.article.title }}</span>
            </router-link>
            <span v-else />
            <router-link
              v-if="neighbors.next"
              :to="`/docs/${neighbors.next.section.slug}/${neighbors.next.article.slug}`"
              class="docs-nav-bottom__link docs-nav-bottom__link--next"
            >
              <span class="docs-nav-bottom__label">Следующая →</span>
              <span class="docs-nav-bottom__title">{{ neighbors.next.article.title }}</span>
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { sections, findArticle } from '@/assets/docs/manifest.js'

const mdContext = require.context('@/assets/docs', true, /\.md$/)

marked.setOptions({
  gfm: true,
  breaks: false,
  headerIds: true,
  mangle: false,
})

export default {
  name: 'DocsPage',
  data() {
    return {
      sections,
      html: '',
      loading: false,
      error: false,
      isMobile: typeof window !== 'undefined' && window.innerWidth <= 768,
    }
  },
  computed: {
    current() {
      return findArticle(this.$route.params.section, this.$route.params.article)
    },
    flatArticles() {
      const list = []
      for (const section of this.sections) {
        for (const article of section.articles) {
          list.push({ section, article })
        }
      }
      return list
    },
    neighbors() {
      if (!this.current) return null
      const idx = this.flatArticles.findIndex(
        x => x.section.slug === this.current.section.slug
          && x.article.slug === this.current.article.slug,
      )
      if (idx === -1) return null
      return {
        prev: idx > 0 ? this.flatArticles[idx - 1] : null,
        next: idx < this.flatArticles.length - 1 ? this.flatArticles[idx + 1] : null,
      }
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler() { this.load() },
    },
  },
  mounted() {
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onResize() {
      this.isMobile = window.innerWidth <= 768
    },
    async load() {
      this.error = false
      if (!this.current) {
        this.error = true
        this.html = ''
        return
      }
      this.loading = true
      try {
        const key = `./${this.current.section.slug}/${this.current.article.slug}.md`
        const raw = mdContext(key).default || mdContext(key)
        const rendered = marked.parse(raw)
        this.html = DOMPurify.sanitize(rendered, { ADD_ATTR: ['target'] })
      } catch (e) {
        this.error = true
        this.html = ''
      } finally {
        this.loading = false
        this.$nextTick(() => window.scrollTo({ top: 0, behavior: 'auto' }))
      }
    },
    onContentClick(e) {
      const link = e.target.closest('a')
      if (!link) return
      const href = link.getAttribute('href') || ''
      if (href.startsWith('/docs/')) {
        e.preventDefault()
        if (href !== this.$route.fullPath) this.$router.push(href)
      } else if (/^https?:\/\//i.test(href)) {
        link.setAttribute('target', '_blank')
        link.setAttribute('rel', 'noopener noreferrer')
      }
    },
  },
}
</script>

<style scoped>
.page-hero {
  background: linear-gradient(135deg, #1E3A8A, #2563EB);
  color: white;
  padding: 140px 0 80px;
}
.page-hero__title { font-size: 2.5rem; font-weight: 800; margin: 12px 0 16px; }
.page-hero__text { font-size: 1.125rem; opacity: 0.85; }

.docs-layout {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 48px;
  align-items: start;
}
.docs-nav-wrap { position: sticky; top: 100px; }
.docs-nav-mobile { width: 100%; }
.docs-nav-mobile__summary {
  display: none;
  padding: 12px 16px;
  background: var(--primary-light);
  border-radius: var(--radius-sm);
  color: var(--primary);
  font-weight: 600;
  cursor: pointer;
  list-style: none;
  align-items: center;
  justify-content: space-between;
}
.docs-nav-mobile__summary::-webkit-details-marker { display: none; }
.docs-nav-mobile__chevron {
  transition: transform 0.2s;
}
.docs-nav-mobile[open] .docs-nav-mobile__chevron { transform: rotate(180deg); }

.docs-nav { display: block; }
.docs-nav__group {
  font-size: 0.75rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.08em; color: var(--gray); padding: 8px 0 4px;
  margin-top: 16px;
}
.docs-nav__group:first-child { margin-top: 0; }
.docs-nav__link {
  display: block; padding: 8px 12px; border-radius: var(--radius-sm);
  font-size: 0.9375rem; color: var(--gray); text-decoration: none;
  transition: var(--transition);
}
.docs-nav__link:hover,
.docs-nav__link.active {
  color: var(--primary);
  background: var(--primary-light);
  font-weight: 500;
}

.docs-content__header {
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 28px;
}
.docs-content__breadcrumb {
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--gray);
  font-weight: 600;
  margin-bottom: 8px;
}
.docs-content__header h2 { font-size: 1.75rem; font-weight: 700; }
.docs-content__body { font-size: 1rem; color: var(--dark); line-height: 1.7; }
.docs-content__body--state p { color: var(--gray); }

.markdown-body >>> h2 {
  font-size: 1.5rem; font-weight: 700; margin: 32px 0 12px;
}
.markdown-body >>> h3 {
  font-size: 1.25rem; font-weight: 600; margin: 24px 0 8px;
}
.markdown-body >>> h4 {
  font-size: 1.0625rem; font-weight: 600; margin: 20px 0 6px;
}
.markdown-body >>> p { margin: 0 0 14px; color: var(--gray); }
.markdown-body >>> ul,
.markdown-body >>> ol { margin: 0 0 16px; padding-left: 24px; color: var(--gray); }
.markdown-body >>> li { margin-bottom: 6px; }
.markdown-body >>> a {
  color: var(--primary);
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: var(--transition);
}
.markdown-body >>> a:hover { border-bottom-color: var(--primary); }
.markdown-body >>> code {
  background: var(--primary-light);
  color: var(--primary);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.875em;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
}
.markdown-body >>> pre {
  background: #0f172a;
  color: #e2e8f0;
  padding: 16px 20px;
  border-radius: var(--radius-sm);
  overflow-x: auto;
  margin: 16px 0 20px;
  font-size: 0.875rem;
  line-height: 1.6;
}
.markdown-body >>> pre code {
  background: transparent;
  color: inherit;
  padding: 0;
}
.markdown-body >>> blockquote {
  border-left: 4px solid var(--primary);
  background: var(--primary-light);
  padding: 12px 16px;
  margin: 16px 0;
  border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
}
.markdown-body >>> blockquote p { margin: 0; color: var(--dark); }
.markdown-body >>> img {
  max-width: 100%;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border);
  margin: 12px 0 20px;
  display: block;
}
.markdown-body >>> table {
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0 20px;
  font-size: 0.9375rem;
}
.markdown-body >>> th,
.markdown-body >>> td {
  border: 1px solid var(--border);
  padding: 10px 14px;
  text-align: left;
}
.markdown-body >>> th {
  background: var(--primary-light);
  font-weight: 600;
  color: var(--dark);
}
.markdown-body >>> hr {
  border: none;
  border-top: 1px solid var(--border);
  margin: 28px 0;
}

.docs-nav-bottom {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 48px;
  padding-top: 24px;
  border-top: 1px solid var(--border);
}
.docs-nav-bottom__link {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 18px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  text-decoration: none;
  transition: var(--transition);
}
.docs-nav-bottom__link:hover {
  border-color: var(--primary);
  background: var(--primary-light);
}
.docs-nav-bottom__link--next { text-align: right; }
.docs-nav-bottom__label {
  font-size: 0.8125rem;
  color: var(--gray);
}
.docs-nav-bottom__title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--dark);
}

@media (max-width: 768px) {
  .docs-layout { grid-template-columns: 1fr; gap: 24px; }
  .docs-nav-wrap { position: static; }
  .docs-nav-mobile__summary { display: flex; }
  .docs-nav-mobile:not([open]) .docs-nav { display: none; }
  .docs-nav { margin-top: 12px; padding: 0 4px; }
  .page-hero__title { font-size: 1.875rem; }
  .docs-nav-bottom { grid-template-columns: 1fr; }
  .docs-nav-bottom__link--next { text-align: left; }
}
</style>
